import React, { useState } from "react";
import { View, Text, StyleSheet, Modal, Pressable, Image } from "react-native";
import * as Animatable from "react-native-animatable";
import metrics, { normalize } from "@constants/metrics";
import CloseIcon from "@assets/svg/CloseCircleGrayishBg";
import Button from "@components/Button";
import SuccessIcon from "@assets/svg/SuccessIcon";
import color from "@constants/color";

import t from "@constants/localization";

const PATH = "screens/SupportTree/SupportTickets/ReasonModal";

const emojiRating = {
  1: "https://pub-storage-svc.citymall.live/1628741456867Terrible_deselect.png",
  2: "https://pub-storage-svc.citymall.live/1628741186130Bad_deselect.png",
  3: "https://pub-storage-svc.citymall.live/1628741533212Okay_deselect.png",
};

export default function ReasonModal(props) {
  const {
    showReasonsModal = false,
    onCloseClick = () => {},
    clTicketDisSatisfactionReasons = [],
    selectedRating,
    onSubmit = () => {},
    handleReloadList,
  } = props || {};
  const [selectedReason, setSelectedReason] = useState([]);
  const [showSuccessDialog, setShowSuccessDialog] = useState(false);
  const isButtonDisable = selectedReason.length === 0;

  const submitButtonClick = (cancelled = false) => {
    if (cancelled) {
      onSubmit(selectedReason, cancelled);
      onCloseClick(false);
      return;
    }
    onSubmit(selectedReason, cancelled);
    setShowSuccessDialog(true);
  };

  return (
    <Modal
      animationType="fade"
      transparent={true}
      visible={showReasonsModal}
      statusBarTranslucent
    >
      <Animatable.View
        duration={300}
        useNativeDriver={true}
        delay={0}
        animation="slideInUp"
        style={styles.centeredView}
      >
        <View
          style={[styles.modalView, showSuccessDialog ? { height: 250 } : {}]}
        >
          <View style={{ marginTop: 10, flexDirection: "column" }}>
            <View style={{ alignItems: "flex-end" }}>
              <Pressable
                onPress={() => {
                  if (showSuccessDialog) {
                    handleReloadList();
                    setShowSuccessDialog(false);
                    setSelectedReason([]);
                    onCloseClick(false);
                  } else {
                    onCloseClick(false);
                    setSelectedReason([]);
                  }
                }}
              >
                <CloseIcon />
              </Pressable>
            </View>
          </View>
          {!showSuccessDialog ? (
            <>
              <View style={{ alignItems: "center" }}>
                <View style={styles.emojiContainer}>
                  <Image
                    source={{
                      uri: emojiRating[selectedRating],
                    }}
                    style={{ width: 32, height: 32 }}
                  />
                </View>
                <View style={{ marginTop: 10, marginBottom: 5 }}>
                  <Text
                    style={{
                      fontSize: 14,
                      fontFamily: "NotoSansDevanagari-SemiBold",
                    }}
                  >
                    {t(`${PATH}/whatYouDintLike`)}
                  </Text>
                </View>
                <View style={styles.reasonContainer}>
                  {Array.isArray(clTicketDisSatisfactionReasons) &&
                    clTicketDisSatisfactionReasons.map((item) => {
                      const isSelected = selectedReason.includes(item.value);
                      return (
                        <Pressable
                          onPress={() => {
                            if (isSelected) {
                              setSelectedReason(
                                selectedReason.filter(
                                  (value) => value !== item.value,
                                ),
                              );
                            } else {
                              setSelectedReason([
                                ...selectedReason,
                                item.value,
                              ]);
                            }
                          }}
                          style={[
                            styles.reasonLabelContainer,
                            {
                              backgroundColor: isSelected
                                ? "rgba(0, 0, 0, 1)"
                                : "rgba(0, 0, 0, 0.02)",
                            },
                          ]}
                        >
                          <Text
                            style={{
                              color: isSelected
                                ? color.primaryBg
                                : "rgba(0, 0, 0, 1)",
                              fontSize: 12,
                              fontFamily: "NotoSansDevanagari-Regular",
                            }}
                          >
                            {item.label}
                          </Text>
                        </Pressable>
                      );
                    })}
                </View>
              </View>
              <Button
                style={{
                  padding: 15,
                }}
                containerStyle={{ marginTop: 25 }}
                buttonWidth="100%"
                btnText={t(`${PATH}/submit`)}
                buttonFontType="head1"
                height={56}
                borderRadius={10}
                onPress={submitButtonClick}
                disabled={isButtonDisable}
              />
            </>
          ) : (
            <View style={styles.thanksContainer}>
              <View style={styles.thanksSubContainer}>
                <SuccessIcon />
                <Text style={styles.thanksText}>{t(`${PATH}/thankyou`)}</Text>
                <Text style={styles.thanksSubText}>
                  {t(`${PATH}/forRating`)}
                </Text>
              </View>
            </View>
          )}
        </View>
      </Animatable.View>
    </Modal>
  );
}

const styles = StyleSheet.create({
  centeredView: {
    flex: 1,
    justifyContent: "center",
    alignItems: "center",
    width: metrics.screenWidth,
    padding: metrics.margin2,
    backgroundColor: "rgba(0,0,0,0.5)",
  },
  modalView: {
    width: "100%",
    backgroundColor: "white",
    paddingHorizontal: 16,
    maxHeight: 300,
    height: 300,
    borderRadius: 10,
    shadowColor: "rgba(0, 0, 0, 0.2)",
    shadowOffset: {
      width: 0,
      height: 2,
    },
    shadowOpacity: 0.25,
    shadowRadius: 4,
    elevation: 5,
  },
  thanksText: {
    marginVertical: normalize(10),
    fontFamily: "NotoSansDevanagari-SemiBold",
    fontSize: 18,
  },
  thanksSubText: {
    color: color.lightBlack,
    fontFamily: "NotoSansDevanagari-Regular",
    fontSize: 12,
  },
  thanksContainer: {
    flex: 1,
    flexDirection: "row",
    justifyContent: "center",
  },
  thanksSubContainer: {
    marginTop: normalize(20),
    marginBottom: normalize(30),
    alignItems: "center",
  },
  reasonContainer: {
    flexDirection: "row",
    flexWrap: "wrap",
    justifyContent: "center",
    alignItems: "center",
  },
  emojiContainer: {
    width: 32,
    height: 32,
    justifyContent: "center",
    alignItems: "center",
  },
  reasonLabelContainer: {
    paddingVertical: 7,
    paddingHorizontal: 16,
    paddingBottom: 4,
    borderWidth: 1,
    borderColor: "rgba(0, 0, 0, 0.5)",
    borderRadius: 40,
    marginVertical: 6,
    marginRight: 6,
  },
});
